import { Input, DatePicker, Select as AntSelect, Switch } from "antd";
import searchIcon from "../../../../assets/icons/search-icon.png";
import { Select, Button1, TagInput } from "../../../index";
import uploadIcon from "../../../../assets/icons/upload-icon.png";
import { useEffect, useRef, useState } from "react";
import { beneficiariesApi, conversionsApi, transfersApi } from "../../../../api/endpoints";
import {
  DESTINATION_COUNTRIES,
  currencyForCountry,
} from "../../../../data/destinationCountries";

// Recipient picks a destination country; the currency is derived from it.
const DEST_COUNTRY_OPTIONS = DESTINATION_COUNTRIES.map((c) => ({
  label: `${c.flag} ${c.name} (${c.currency})`,
  value: c.iso,
}));

const EmailPhoneCard = ({ sourceAccountId, sourceAccount }) => {
  const [isChecked, setIsChecked] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [matched, setMatched] = useState(null);
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarError, setAvatarError] = useState("");

  // Transfer + FX state
  const [amount, setAmount] = useState("");
  const [sourceTransferCurrency, setSourceTransferCurrency] = useState(sourceAccount?.currency || "AUD");
  const [destCountry, setDestCountry] = useState("PK");
  const [destCurrency, setDestCurrency] = useState("PKR");
  const [quote, setQuote] = useState(null);
  const [indicativeRate, setIndicativeRate] = useState(null);

  const debounceRef = useRef(null);
  const quoteDebounceRef = useRef(null);
  const avatarInputRef = useRef(null);

  // Remittances are funded from the AUD wallet — the source is always AUD.
  const sourceCurrency = sourceTransferCurrency || sourceAccount?.currency || "AUD";
  useEffect(() => {
    setSourceTransferCurrency(sourceAccount?.currency || "AUD");
  }, [sourceAccount?.currency]);

  // Indicative mid-market rate as soon as both currencies are picked.
  useEffect(() => {
    if (!sourceCurrency || !destCurrency || sourceCurrency === destCurrency) {
      setIndicativeRate(null);
      return;
    }
    let cancelled = false;
    conversionsApi
      .rate(sourceCurrency, destCurrency)
      .then((r) => !cancelled && setIndicativeRate(r))
      .catch(() => !cancelled && setIndicativeRate(null));
    return () => {
      cancelled = true;
    };
  }, [sourceCurrency, destCurrency]);

  // Live binding quote (with fees) once the user enters an amount.
  useEffect(() => {
    if (quoteDebounceRef.current) clearTimeout(quoteDebounceRef.current);
    if (
      !sourceAccountId ||
      !amount ||
      isNaN(Number(amount)) ||
      Number(amount) <= 0 ||
      sourceCurrency === destCurrency
    ) {
      setQuote(null);
      return;
    }
    quoteDebounceRef.current = setTimeout(() => {
      transfersApi
        .quote({
          sourceAccountId,
          sourceAmount: Number(amount),
          destCurrency,
          transferType: "INTERNATIONAL",
        })
        .then(setQuote)
        .catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(quoteDebounceRef.current);
  }, [sourceAccountId, amount, destCurrency, sourceCurrency]);

  const recipientGets =
    quote?.destAmount != null
      ? Number(quote.destAmount).toFixed(2)
      : amount && indicativeRate?.midRate
      ? (Number(amount) * Number(indicativeRate.midRate)).toFixed(2)
      : "";
  const handleAvatarFile = (file) => {
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      setAvatarError("Please upload an image file.");
      return;
    }

    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setAvatarError("");
  };

  const removeAvatar = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview("");
    setAvatarError("");
    if (avatarInputRef.current) {
      avatarInputRef.current.value = "";
    }
  };

  useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const term = searchTerm.trim();
    if (!term) {
      setMatched(null);
      setNotFound(false);
      setSearching(false);
      return;
    }

    setSearching(true);
    setNotFound(false);

    debounceRef.current = setTimeout(async () => {
      try {
        const list = await beneficiariesApi.list({ search: term });
        if (Array.isArray(list) && list.length > 0) {
          const hit = list[0];
          setMatched(hit);
          setNotFound(false);
          setName(hit.fullName || hit.businessName || "");
          setEmail(hit.email || "");
          setPhone(hit.mobile || "");
        } else {
          setMatched(null);
          setNotFound(true);
        }
      } catch (err) {
        setMatched(null);
        setNotFound(true);
      } finally {
        setSearching(false);
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  return (
    <>
      <div className="col-lg-5 p-3">
        <div className="d-flex flex-column gap-3">
          <span className="fs-5 fw-medium">Email/Phone/Card</span>

          <div className="d-flex flex-column align-items-center gap-2">
            <Input
              placeholder="Enter Email/Phone/Card"
              prefix={<img src={searchIcon} alt="search" />}
              className="p-3 someone-new-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
            {searching && (
              <span className="fs12px fw-medium" style={{ color: "#7D7D7D" }}>
                searching...
              </span>
            )}
            {!searching && matched && (
              <span
                className="fs12px fw-medium"
                style={{ color: "#1bbb6b" }}
              >
                Recipient found, details auto-filled below
              </span>
            )}
            {!searching && notFound && (
              <span className="fs12px fw-medium" style={{ color: "#7D7D7D" }}>
                recipient not found
              </span>
            )}
          </div>

          <div className="d-flex flex-column gap-2 ">
            <label className="fs12px">Name</label>
            <Input
              placeholder="Recipient's Name"
              className="p-3"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Avatar</label>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={(e) => handleAvatarFile(e.target.files?.[0])}
            />
            <button
              type="button"
              className={`avatar-upload-control ${
                avatarPreview ? "has-avatar" : ""
              }`}
              onClick={() => avatarInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleAvatarFile(e.dataTransfer.files?.[0]);
              }}
            >
              {avatarPreview ? (
                <>
                  <img
                    src={avatarPreview}
                    alt="Recipient avatar preview"
                    className="avatar-upload-preview"
                  />
                  <span className="avatar-upload-file-name">
                    {avatarFile?.name}
                  </span>
                </>
              ) : (
                <span className="avatar-upload-placeholder">
                  Upload or drag files here
                </span>
              )}
              <img src={uploadIcon} alt="" className="avatar-upload-icon" />
            </button>
            {avatarError && (
              <span className="fs12px" style={{ color: "#c62828" }}>
                {avatarError}
              </span>
            )}
            {avatarPreview && (
              <button
                type="button"
                className="avatar-remove-button"
                onClick={removeAvatar}
              >
                Remove avatar
              </button>
            )}
          </div>

          <div className="d-flex flex-column gap-2">
            <span className="fs12px">Email</span>
            <Input
              placeholder="example@gmail.com"
              className="p-3"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <span className="fs12px">Phone Number</span>
            <Input
              placeholder="XXXX-XXXX-XXXX"
              className="p-3"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="col-lg-6 p-3">
        <div className="d-flex flex-column gap-3">
          <span className="fs-5 fw-medium">Transfer Details</span>

          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-3 ">
              <div className="d-flex flex-column gap-2 w-75 ">
                <label className="fs12px">Amount you want to send</label>
                <Input
                  className="p-3 w-100"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div className="d-flex flex-column gap-2 new-transfer-currency-field">
                <label className="fs12px">Currency</label>
                <AntSelect
                  value={sourceCurrency}
                  disabled
                  options={[{ label: sourceCurrency, value: sourceCurrency }]}
                  className="new-transfer-currency-select"
                  style={{ width: "100%" }}
                  popupClassName="new-transfer-currency-popup"
                />
              </div>
            </div>

            <span className="fs12px" style={{ color: "#7D7D7D" }}>
              Available balance :{" "}
              <span className="text-black">
                {sourceAccount
                  ? `${Number(sourceAccount.availableBalance).toLocaleString()} ${sourceAccount.currency}`
                  : "0.00"}
              </span>
            </span>

            {(() => {
              const r = Number(quote?.midRate ?? indicativeRate?.midRate);
              if (!(Number.isFinite(r) && r > 0) || sourceCurrency === destCurrency) return null;
              return (
                <span className="fs12px" style={{ color: "#7D7D7D" }}>
                  {quote ? "Live" : "Indicative"} rate: 1 {sourceCurrency} ={" "}
                  <span className="fw-semibold" style={{ color: "#1bbb6b" }}>
                    {r.toFixed(6)} {destCurrency}
                  </span>
                </span>
              );
            })()}

            {quote?.fee != null && Number(quote.fee) > 0 && (
              <span className="fs12px" style={{ color: "#7D7D7D" }}>
                Transfer fee:{" "}
                <span className="fw-semibold text-black">
                  {Number(quote.fee).toFixed(2)} {sourceCurrency}
                </span>
              </span>
            )}
          </div>

          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-3 ">
              <div className="d-flex flex-column gap-2 w-75">
                <label className="fs12px">Recipients will get</label>
                <Input
                  className="p-3 w-100"
                  placeholder="0.00"
                  value={recipientGets}
                  readOnly
                />
              </div>
              <div className="d-flex flex-column gap-2 new-transfer-currency-field">
                <label className="fs12px">Country</label>
                <AntSelect
                  showSearch
                  optionFilterProp="label"
                  value={destCountry}
                  onChange={(iso) => {
                    setDestCountry(iso);
                    setDestCurrency(currencyForCountry(iso) || "PKR");
                  }}
                  options={DEST_COUNTRY_OPTIONS}
                  className="new-transfer-currency-select"
                  style={{ width: "100%" }}
                  popupClassName="new-transfer-currency-popup"
                />
              </div>
            </div>
          </div>

          <div className="d-flex flex-column gap-2 mt-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="fs-5 fw-bold">
                Schedule or set recurring transfer
              </span>
              <Switch onClick={setIsChecked} checked={isChecked} />
            </div>

            {isChecked && (
              <div className="d-flex flex-column gap-3 mt-4">
                <div className="d-flex flex-column gap-2">
                  <label className="fs12px">Start date</label>
                  <DatePicker
                    placeholder="Dec 28, 2026"
                    className="p-3"
                    disabled={!isChecked}
                  />
                </div>
                <div className="d-flex flex-column gap-2">
                  <label className="fs12px">Repeat</label>
                  <Select
                    defaultValue="Monthly"
                    options={[
                      { name: "Once", value: "Once" },
                      { name: "Weekly", value: "Weekly" },
                      { name: "Monthly", value: "Monthly" },
                      { name: "Yearly", value: "Yearly" },
                    ]}
                    className="p-3"
                    disabled={!isChecked}
                  />
                </div>
                <div className="d-flex flex-column gap-2">
                  <label className="fs12px">End date</label>
                  <DatePicker
                    placeholder="Feb 15, 2027"
                    className="p-3"
                    disabled={!isChecked}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="d-flex flex-column gap-3 mt-3">
            <span className="fs-5 fw-bold">Additional Details</span>
            <div className="d-flex flex-column gap-2 ">
              <label className="fs12px ">Category</label>
              <Select
                defaultValue="Choose Category"
                className="rounded-2 p-3"
              />
            </div>
            <div className="d-flex flex-column gap-2 ">
              <label className="fs12px ">Tags</label>
              <TagInput
                className="border py-3 "
                inputBgColor="transparent"
                tagColor="#1bbb6b"
                tagTextColor="#000"
                limit={10}
                placeholder="Add up to 10 Tags"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-md-end justify-content-between p-2 gap-3">
        <Button1 buttonName={"Invite"} className={"border"} />
        <Button1 buttonName={"Share"} className={"btn-outline-success"} />
        <Button1 buttonName={"Create"} className="btn-dark " />
      </div>
    </>
  );
};

export default EmailPhoneCard;
